import { SpinnerImage } from '@/components/Spinner/Spinner.styles';
import { NotiButtonState } from '@/constants';
import { useResponsive } from '@/hooks';
import { ButtonColorKey, ButtonSize, IconAlignment, ResponsiveCollection } from '@/interfaces';
import { useTrackingDetailsContext } from '@/modules/pmt/components/TrackingDetails/context';
import { useMyParcelsContext, useShipmentDetailsContext } from '@/modules/pmt/context';
import CheckIcon from '@/public/images/icons/check.svg';
import NotificationsIcon from '@/public/images/icons/notifications.svg';
import { gtmViewNotified } from '@/utils';
import { useEffect } from 'react';
import { NotiButton, NotiContainer } from './NotificationSection.styles';
import { useSubscribeNoti } from './hooks';
import { isNotiAllowed } from './utils';

export const NotificationSection = () => {
  const { isDesktopPMT } = useResponsive([ResponsiveCollection.DesktopPMT]);
  const {
    notificationTranslation: { mobileText, desktopText, buttonText, identifier },
  } = useMyParcelsContext();
  const { currentShipment } = useShipmentDetailsContext();
  const { notiButtonStateByParcel } = useTrackingDetailsContext();
  const { loginState, notiButtonState, handleNotiButtonClick } = useSubscribeNoti();

  useEffect(() => {
    // GTM triggered only for default state of the button (not in process, not subscribed)
    if (!currentShipment || notiButtonState !== NotiButtonState.Default) return;
    loginState && gtmViewNotified(loginState, identifier);
  }, [notiButtonState, currentShipment]);

  if (!mobileText && !desktopText) return null;

  const displayNoti = isNotiAllowed({
    shipment: currentShipment,
    notiButtonState,
    notiButtonStateByParcel,
  });

  if (!displayNoti) return null;

  const renderButton = (isMobile?: boolean) => {
    let Icon = null; // Icon is checked icon or spinner icon
    let hasDefaultCursor = false; // If button state is DONE/HIDDEN, then cursor is default

    switch (notiButtonState) {
      case NotiButtonState.Default:
        Icon = NotificationsIcon;
        break;
      case NotiButtonState.Processing:
        Icon = SpinnerImage;
        break;
      case NotiButtonState.Subscribed:
      case NotiButtonState.Hidden:
        Icon = CheckIcon;
        hasDefaultCursor = true;
        break;
    }

    return (
      <NotiButton
        truncate={2}
        size={ButtonSize.Large}
        isPMTButton
        color={ButtonColorKey.Yellow}
        onClick={handleNotiButtonClick}
        icon={Icon}
        alignment={IconAlignment.Left}
        disabled={notiButtonState === NotiButtonState.Processing}
        // custom
        isMobile={isMobile}
        hasDefaultCursor={hasDefaultCursor}
      >
        {buttonText}
      </NotiButton>
    );
  };

  return (
    <NotiContainer hidden={notiButtonState === NotiButtonState.Hidden}>
      {isDesktopPMT && renderButton()}
      {!isDesktopPMT && renderButton(true)}
    </NotiContainer>
  );
};
